import type { Product } from 'payments-api/model';
import {
  useGetProductsSuspense,
  usePostProducts,
} from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { FormButtons } from '@fragment/ui/src/components/Button/ButtonGroup/FormButtons';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';

import { FILLER_BORDER, GRID_CONTAINER, TILE_BORDER } from './constants';
import { GridTile } from './GridTile';
import { ItemColorSquare } from './ItemColorSquare';

const ProductTile = ({
  product,
  goToProduct,
}: {
  product: Product;
  goToProduct: (product: Product) => void;
}) => (
  <GridTile
    onClick={() => goToProduct(product)}
    data-testid="product-details-button"
    icon={<ItemColorSquare productId={product.code} size={24} hoverAnimate />}
    primary={product.description ?? product.code}
    secondary={product.description ? product.code : undefined}
  />
);

const ProductListRow = ({
  product,
  goToProduct,
}: {
  product: Product;
  goToProduct: (product: Product) => void;
}) => (
  <button
    type="button"
    onClick={() => goToProduct(product)}
    aria-label={`product-details-${product.code}`}
    data-testid="product-details-button"
    className="flex items-center gap-x-f1 px-f1 h-f4 text-xmd border-b-[0.5px] border-main-200 hover:bg-negative w-full text-left"
  >
    <ItemColorSquare productId={product.code} />
    <span className="text-main text-xmd flex-1 truncate">{product.code}</span>
    {product.description && (
      <span className="text-main-500 text-xmd flex-1 truncate">
        {product.description}
      </span>
    )}
  </button>
);

const DraftProductRow = ({
  onSubmit,
  onCancel,
  isPending,
}: {
  onSubmit: (code: string, description: string | undefined) => Promise<void>;
  onCancel: () => void;
  isPending: boolean;
}) => {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | undefined>();
  const handleSubmit = useCallback(async () => {
    setError(undefined);
    try {
      await onSubmit(code.trim(), description.trim() || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create product');
    }
  }, [code, description, onSubmit]);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- grandfathered
        handleSubmit();
      }}
      className="col-span-full flex flex-col gap-y-f1 w-full p-f1 min-h-f12 border-r-[0.5px] border-b-[0.5px] border-main-300"
    >
      <div className="flex flex-row items-center gap-x-f1 w-full">
        <TextInput
          padding="px-f1 py-f0"
          width="w-full"
          height="h-fit"
          align="left"
          placeholder="Product code"
          size="md"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          data-testid="draft-product-code"
        />
      </div>
      <TextInput
        padding="px-f1 py-f0"
        width="w-full"
        height="h-fit"
        align="left"
        placeholder="Description"
        size="md"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        data-testid="draft-product-description"
      />
      {error && <ErrorText>{error}</ErrorText>}
      <FormButtons
        type="secondary"
        cancelButtonLabel="Cancel"
        onCancel={onCancel}
        submitButtonLabel={isPending ? 'Creating...' : 'Create'}
        disabled={isPending || !code.trim()}
        submitButtonTestId="draft-product-submit-button"
        cancelButtonTestId="draft-product-cancel-button"
      />
    </form>
  );
};

type CatalogProductTableContentProps = {
  onProductClick: (product: Product) => void;
  viewMode: 'grid' | 'list';
  showDraftProduct: boolean;
  onDraftClose: () => void;
};

export const CatalogProductTableContent = ({
  onProductClick,
  viewMode,
  showDraftProduct,
  onDraftClose,
}: CatalogProductTableContentProps) => {
  const { data: productsData } = useGetProductsSuspense();
  const queryClient = useQueryClient();
  const createProductMutation = usePostProducts();

  const products: Product[] = productsData.data;

  const handleDraftProductSubmit = useCallback(
    async (code: string, description: string | undefined) => {
      await createProductMutation.mutateAsync({
        data: {
          code,
          ...(description !== undefined && { description }),
        },
      });
      await queryClient.invalidateQueries({ queryKey: ['/products'] });
      onDraftClose();
    },
    [createProductMutation, queryClient, onDraftClose]
  );

  if (viewMode === 'list') {
    return (
      <div className="flex flex-col w-full">
        <div className="grid grid-flow-col auto-cols-fr gap-x-[1ch] px-f1 items-center h-f4 text-xmd text-main-500 border-b-[0.5px] border-main-200">
          <div>Name</div>
          <div className="text-right">Price</div>
        </div>
        {products.map((product) => (
          <ProductListRow
            key={product.id}
            product={product}
            goToProduct={onProductClick}
          />
        ))}
        {showDraftProduct && (
          <DraftProductRow
            onSubmit={handleDraftProductSubmit}
            onCancel={onDraftClose}
            isPending={createProductMutation.isPending}
          />
        )}
      </div>
    );
  }

  return (
    <div className={GRID_CONTAINER}>
      {products.map((product) => (
        <ProductTile
          key={product.id}
          product={product}
          goToProduct={onProductClick}
        />
      ))}
      {showDraftProduct && (
        <DraftProductRow
          onSubmit={handleDraftProductSubmit}
          onCancel={onDraftClose}
          isPending={createProductMutation.isPending}
        />
      )}
      {Array.from({ length: 8 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`filler-${i}`} className={FILLER_BORDER} />
      ))}
    </div>
  );
};

type CatalogProductTableProps = {
  onProductClick: (product: Product) => void;
  viewMode: 'grid' | 'list';
  showDraftProduct: boolean;
  onDraftClose: () => void;
};

const SKELETON_MAX_TILES = 12;

const CatalogProductTableLoading = () => {
  const [visibleTiles, setVisibleTiles] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleTiles((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_TILES) {
          directionRef.current = -1;
          return SKELETON_MAX_TILES;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className={GRID_CONTAINER}>
      <div className={clsx('aspect-[8/9]', TILE_BORDER)} />
      {/* eslint-disable react/no-array-index-key */}
      {Array.from({ length: visibleTiles }).map((_, i) => (
        <div
          key={`skeleton-${i}`}
          className={clsx('aspect-[8/9]', TILE_BORDER)}
        />
      ))}
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={`filler-${i}`} className={FILLER_BORDER} />
      ))}
      {/* eslint-enable react/no-array-index-key */}
    </div>
  );
};

export const CatalogProductTable = ({
  onProductClick,
  viewMode,
  showDraftProduct,
  onDraftClose,
}: CatalogProductTableProps): JSX.Element => (
  <Suspense fallback={<CatalogProductTableLoading />}>
    <CatalogProductTableContent
      onProductClick={onProductClick}
      viewMode={viewMode}
      showDraftProduct={showDraftProduct}
      onDraftClose={onDraftClose}
    />
  </Suspense>
);
